import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ShowCard from '@/components/base/ShowCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, shows, heroShows, latestShows, recommendedShows, hotShows } from '@/mocks/shows';

export default function Home() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [filteredShows, setFilteredShows] = useState(shows);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredShows(shows);
    } else {
      setFilteredShows(shows.filter((s) => s.categorySlug === activeCategory));
    }
  }, [activeCategory]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % heroShows.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const currentHeroShow = heroShows[currentHeroIndex];

  const heroBackgrounds = heroShows.map((s) => s.heroImageUrl);

  return (
    <>
      <SchemaOrg type="WebPage" data={{
        name: '人人美剧 - 首页',
        description: '人人美剧是您追剧的最佳平台，提供海量最新美剧、经典美剧免费在线观看，支持高清播放、中英文字幕，涵盖剧情、悬疑、科幻、喜剧等全类型。',
        url: 'https://www.renrenmeiju.com/',
        isPartOf: { '@type': 'WebSite', name: '人人美剧', url: 'https://www.renrenmeiju.com/' },
      }} />

      <Navbar />

      <main>
        <section className="relative h-[420px] md:h-[600px] overflow-hidden">
          {heroBackgrounds.map((bg, index) => (
            <div
              key={index}
              className="absolute inset-0 transition-opacity duration-1000"
              style={{ opacity: index === currentHeroIndex ? 1 : 0 }}
            >
              <img src={bg} alt="" className="w-full h-full object-cover object-center" />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="px-4 md:px-6 w-full">
              <div className="max-w-7xl mx-auto">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="bg-primary-500 text-background-50 text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap">
                      {currentHeroShow.rating}
                    </span>
                    <span className="text-background-50/80 text-sm">{currentHeroShow.category}</span>
                    <span className="text-background-50/50">·</span>
                    <span className="text-background-50/80 text-sm">{currentHeroShow.seasons}季</span>
                  </div>
                  <h2 className="font-heading text-2xl md:text-4xl text-background-50 mb-2">
                    {currentHeroShow.title}
                  </h2>
                  <p className="text-sm md:text-base text-background-50/80 mb-4 hidden sm:block max-w-lg">
                    {currentHeroShow.englishTitle}
                  </p>
                  <p className="text-sm text-background-50/70 mb-6 line-clamp-2 max-w-lg">
                    {currentHeroShow.description}
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => navigate(`/article/${currentHeroShow.id}`)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-play-circle-line"></i>
                      </span>
                      立即观看
                    </button>
                    <button
                      onClick={() => navigate(`/category/${currentHeroShow.categorySlug}`)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50/20 hover:bg-background-50/30 text-background-50 backdrop-blur-sm rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      更多{currentHeroShow.category}美剧
                    </button>
                  </div>

                  <div className="flex items-center gap-2 mt-8">
                    {heroShows.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentHeroIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                          index === currentHeroIndex
                            ? 'bg-background-50 w-6'
                            : 'bg-background-50/40 hover:bg-background-50/70'
                        }`}
                        aria-label={`切换到第${index + 1}张`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg md:text-xl text-foreground-950">分类浏览</h2>
              <button
                onClick={() => navigate('/category/all')}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </button>
            </div>
            <CategoryTabs categories={categories} activeSlug={activeCategory} onCategoryChange={setActiveCategory} />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-6">
              {filteredShows.slice(0, 12).map((show) => (
                <ShowCard key={show.id} show={show} size="small" />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-lg md:text-xl text-foreground-950">最新更新</h2>
                <p className="text-sm text-foreground-600 mt-1">每日更新，第一时间追上热门美剧</p>
              </div>
              <button
                onClick={() => navigate('/category/all')}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {latestShows.map((show) => (
                <ShowCard key={show.id} show={show} size="medium" />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <h2 className="font-heading text-lg md:text-xl text-foreground-950">热门推荐</h2>
              <p className="text-sm text-foreground-600 mt-1">千万用户的选择，口碑神剧不容错过</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {hotShows.map((show) => (
                <ShowCard key={`hot-${show.id}`} show={show} size="medium" />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <h2 className="font-heading text-lg md:text-xl text-foreground-950">编辑精选</h2>
              <p className="text-sm text-foreground-600 mt-1">精心挑选，每一部都是经典</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {recommendedShows.map((show) => (
                <ShowCard key={`rec-${show.id}`} show={show} size="medium" />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl text-foreground-950 text-center mb-8">常见问题</h2>
            <div className="space-y-3">
              {[
                { q: '人人美剧是否完全免费？', a: '是的，人人美剧平台上的所有美剧内容均免费提供给用户在线观看，无需支付任何费用。我们致力于为广大美剧爱好者提供最便捷的追剧体验。' },
                { q: '支持哪些播放清晰度？', a: '人人美剧支持标清、高清（720P）和超清（1080P）等多种清晰度选择。根据您的网络状况，系统会自动推荐最佳播放画质，您也可以手动切换。' },
                { q: '是否提供中英文字幕？', a: '我们为所有美剧提供高质量的中英文字幕。您可以根据个人喜好选择仅显示中文字幕、仅显示英文字幕、同时显示中英双语字幕或关闭字幕。' },
                { q: '美剧资源多久更新一次？', a: '人人美剧团队每日更新美剧资源。对于正在连载的热门美剧，我们通常在美国播出后的24小时内更新中文字幕版本，让您第一时间追到最新剧集。' },
                { q: '可以在手机上观看吗？', a: '当然可以！人人美剧支持全平台访问，包括电脑、平板和手机。在手机上访问我们的网站即可获得优化的移动端观看体验。' },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-lg">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-950 pr-4">{faq.q}</span>
                    <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 md:py-20 bg-secondary-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">开始您的追剧之旅</h2>
            <p className="text-foreground-600 mb-8 max-w-2xl mx-auto">
              海量美剧资源，高清画质，中英双语字幕，每日更新。人人美剧，让追剧成为一种享受。
            </p>
            <div className="flex items-center justify-center gap-3 flex-col sm:flex-row">
              <button
                onClick={() => navigate('/category/all')}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                立即浏览美剧库
              </button>
              <button
                onClick={() => navigate('/about')}
                className="px-6 py-3 bg-background-50 border border-background-200/70 hover:bg-background-100 text-foreground-700 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                了解更多
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}